import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table, Badge, Spinner, Alert, Form,
  Row, Col, Button,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown, faChevronRight, faSync, faCommentDots, faFileExcel,
} from '@fortawesome/free-solid-svg-icons';
import { getReturns, getReturnNotes, exportReturns } from '../services/api';

const PAGE_LIMIT = 50;

// ── Column definitions ─────────────────────────────────────────────────────────
const COLS = [
  { key: 'expand',    label: '',             width: '3%',  align: 'center' },
  { key: 'id',        label: 'Return #',     width: '8%',  align: 'left'   },
  { key: 'sku',       label: 'SKU / Item',   width: '18%', align: 'left'   },
  { key: 'orderRef',  label: 'Order Ref',    width: '10%', align: 'left'   },
  { key: 'authQty',   label: 'Auth Qty',     width: '8%',  align: 'right'  },
  { key: 'retQty',    label: 'Returned Qty', width: '9%',  align: 'right'  },
  { key: 'status',    label: 'Status',       width: '9%',  align: 'left'   },
  { key: 'initiated', label: 'Initiated',    width: '11%', align: 'left'   },
  { key: 'comment',   label: 'Last Comment', width: '24%', align: 'left'   },
];

const TH_STYLE = {
  background: '#212529',
  color: '#fff',
  fontWeight: 600,
  fontSize: '0.8rem',
  whiteSpace: 'nowrap',
  padding: '8px 10px',
  border: 'none',
  borderBottom: '2px solid #495057',
};

const TD_STYLE = {
  padding: '7px 10px',
  verticalAlign: 'middle',
  fontSize: '0.875rem',
  borderBottom: '1px solid #dee2e6',
};

const TABLE_STYLE = {
  tableLayout: 'fixed',
  width: '100%',
  borderCollapse: 'collapse',
  margin: 0,
};

function ColGroup() {
  return (
    <colgroup>
      {COLS.map(c => <col key={c.key} style={{ width: c.width }} />)}
    </colgroup>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────
const STATUS_META = {
  pending:    { label: 'Pending',    bg: 'primary'   },
  in_transit: { label: 'In Transit', bg: 'warning'   },
  received:   { label: 'Received',   bg: 'info'      },
  restocked:  { label: 'Restocked',  bg: 'success'   },
  cancelled:  { label: 'Cancelled',  bg: 'secondary' },
};

const RESTOCK_LABELS = {
  restock:    'Restocked to Inventory',
  quarantine: 'Quarantined',
  dispose:    'Disposed',
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status?.toLowerCase()] || { label: status, bg: 'secondary' };
  return <Badge bg={meta.bg}>{meta.label}</Badge>;
}

function fmtDate(val) {
  if (!val) return '—';
  return new Date(val).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtDateTime(val) {
  if (!val) return '—';
  return new Date(val).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function fmtQty(val) {
  if (val == null) return '—';
  return Number(val).toLocaleString(undefined, { maximumFractionDigits: 3 });
}

// ── Note thread (loaded on demand, cached at parent) ──────────────────────────
function NoteThread({ notes, loading, error }) {
  if (loading) return <Spinner animation="border" size="sm" />;
  if (error)   return <span className="text-danger small">{error}</span>;
  if (!notes || notes.length === 0) return <span className="text-muted small">No comments yet.</span>;

  return (
    <div className="d-flex flex-column gap-2">
      {notes.map(note => (
        <div key={note.idNote} className="p-2 rounded"
          style={{ background: '#fff', border: '1px solid var(--bs-border-color)' }}>
          <div className="d-flex justify-content-between mb-1">
            <span className="fw-semibold small">{note.authorName || 'Staff'}</span>
            <span className="text-muted small">{fmtDateTime(note.createdAt)}</span>
          </div>
          <p className="mb-0 small" style={{ whiteSpace: 'pre-wrap' }}>{note.noteText}</p>
        </div>
      ))}
    </div>
  );
}

// ── Expanded detail panel ─────────────────────────────────────────────────────
function ReturnDetail({ ret, notesCache }) {
  const cached = notesCache.current[ret.idReturn];
  return (
    <div className="p-3" style={{ background: 'var(--bs-gray-100)', borderTop: '1px solid var(--bs-border-color)' }}>
      <Row className="g-3">
        <Col xs={12} md={4}>
          <h6 className="fw-semibold mb-2">Return Details</h6>
          <dl className="mb-0 small" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 12px' }}>
            <dt className="text-muted">Return ID</dt>    <dd>#{ret.idReturn}</dd>
            <dt className="text-muted">SKU</dt>          <dd><code>{ret.sku || '—'}</code></dd>
            <dt className="text-muted">Item</dt>         <dd>{ret.itemName || '—'}</dd>
            <dt className="text-muted">Order Ref</dt>    <dd>{ret.orderRef || '—'}</dd>
            <dt className="text-muted">Auth Qty</dt>     <dd>{fmtQty(ret.authorizedQty)}</dd>
            <dt className="text-muted">Returned Qty</dt> <dd>{fmtQty(ret.returnedQty)}</dd>
            <dt className="text-muted">Initiated</dt>    <dd>{fmtDateTime(ret.initiatedAt)}</dd>
            <dt className="text-muted">Received</dt>     <dd>{fmtDateTime(ret.receivedAt)}</dd>
            {ret.restockMethod && (
              <>
                <dt className="text-muted">Disposition</dt>
                <dd><Badge bg="secondary">{RESTOCK_LABELS[ret.restockMethod] || ret.restockMethod}</Badge></dd>
              </>
            )}
          </dl>
          {ret.notes && (
            <div className="mt-2">
              <span className="text-muted small d-block">Internal Notes</span>
              <p className="small mb-0" style={{ whiteSpace: 'pre-wrap' }}>{ret.notes}</p>
            </div>
          )}
        </Col>
        <Col xs={12} md={8}>
          <h6 className="fw-semibold mb-2 d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faCommentDots} /> Comment History
          </h6>
          <NoteThread
            notes={cached?.data}
            loading={!cached || cached.loading}
            error={cached?.error}
          />
        </Col>
      </Row>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function Returns() {
  const [returns, setReturns]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting]     = useState(false);
  const [error, setError]             = useState(null);
  const [expandedId, setExpandedId]   = useState(null);
  const [page, setPage]               = useState(1);
  const [hasMore, setHasMore]         = useState(false);
  const [totalCount, setTotalCount]   = useState(0);

  // Cache note fetches per return ID: { [idReturn]: { loading, data, error } }
  const notesCache = useRef({});
  const sentinelRef = useRef(null);
  const [, forceUpdate] = useState(0);

  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom]         = useState('');
  const [dateTo, setDateTo]             = useState('');
  const [search, setSearch]             = useState('');

  const buildParams = useCallback((pg) => {
    const params = { page: pg, limit: PAGE_LIMIT };
    if (statusFilter) params.status   = statusFilter;
    if (dateFrom)     params.dateFrom = dateFrom;
    if (dateTo)       params.dateTo   = dateTo;
    if (search)       params.search   = search;
    return params;
  }, [statusFilter, dateFrom, dateTo, search]);

  const fetchReturns = useCallback(async (pg = 1, append = false) => {
    if (pg === 1) setLoading(true); else setLoadingMore(true);
    setError(null);
    try {
      const result = await getReturns(buildParams(pg));
      const items  = result?.items ?? [];
      const pag    = result?.pagination ?? {};
      setReturns(prev => append ? [...prev, ...items] : items);
      setHasMore(pag.hasMore ?? false);
      setTotalCount(pag.total ?? items.length);
      setPage(pg);
    } catch {
      setError('Failed to load returns. Please try again.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [buildParams]);

  useEffect(() => { fetchReturns(1, false); }, [fetchReturns]);

  // Infinite scroll sentinel
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && hasMore && !loadingMore) fetchReturns(page + 1, true); },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, page, fetchReturns]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const params = {};
      if (statusFilter) params.status   = statusFilter;
      if (dateFrom)     params.dateFrom = dateFrom;
      if (dateTo)       params.dateTo   = dateTo;
      if (search)       params.search   = search;
      const response = await exportReturns(params);
      const url  = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', 'returns_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [statusFilter, dateFrom, dateTo, search]);

  const toggleExpand = useCallback((id) => {
    setExpandedId(prev => {
      const next = prev === id ? null : id;
      if (next !== null && !notesCache.current[next]) {
        notesCache.current[next] = { loading: true, data: null, error: null };
        getReturnNotes(next)
          .then(d  => { notesCache.current[next] = { loading: false, data: d,    error: null };               forceUpdate(n => n + 1); })
          .catch(() => { notesCache.current[next] = { loading: false, data: null, error: 'Failed to load.' }; forceUpdate(n => n + 1); });
      }
      return next;
    });
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* ── Filter bar ── */}
      <div style={{ flexShrink: 0, padding: '12px 0 8px', borderBottom: '1px solid #dee2e6' }}>
        <Row className="g-2 align-items-end">
          <Col xs={12} sm={4} md={3}>
            <Form.Label className="small fw-semibold mb-1">Status</Form.Label>
            <Form.Select size="sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All Active</option>
              <option value="pending">Pending</option>
              <option value="in_transit">In Transit</option>
              <option value="received">Received</option>
              <option value="restocked">Restocked</option>
              <option value="cancelled">Cancelled</option>
            </Form.Select>
          </Col>
          <Col xs={12} sm={4} md={3}>
            <Form.Label className="small fw-semibold mb-1">Search</Form.Label>
            <Form.Control
              type="text" size="sm"
              placeholder="SKU, item, or order ref…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </Col>
          <Col xs={6} sm={3} md={2}>
            <Form.Label className="small fw-semibold mb-1">From</Form.Label>
            <Form.Control type="date" size="sm" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </Col>
          <Col xs={6} sm={3} md={2}>
            <Form.Label className="small fw-semibold mb-1">To</Form.Label>
            <Form.Control type="date" size="sm" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </Col>
          <Col xs="auto">
            <Button size="sm" variant="outline-secondary" onClick={() => fetchReturns(1, false)} title="Refresh">
              <FontAwesomeIcon icon={faSync} />
            </Button>
          </Col>
          <Col xs="auto">
            <Button size="sm" variant="outline-success" onClick={handleExport} disabled={exporting} title="Export to Excel">
              <FontAwesomeIcon icon={faFileExcel} className="me-1" />
              {exporting ? 'Exporting…' : 'Export'}
            </Button>
          </Col>
          <Col xs="auto" className="ms-auto">
            <span className="text-muted small">
              {returns.length}{hasMore ? `+ of ${totalCount}` : ''} return{totalCount !== 1 ? 's' : ''}
            </span>
          </Col>
        </Row>
        {error && <Alert variant="danger" className="mt-2 mb-0 py-2">{error}</Alert>}
      </div>

      {/* ── Fixed header table ── */}
      {!loading && returns.length > 0 && (
        <div style={{ flexShrink: 0, overflowX: 'auto', background: '#212529' }}>
          <table style={TABLE_STYLE}>
            <ColGroup />
            <thead>
              <tr>
                {COLS.map(c => (
                  <th key={c.key} style={{ ...TH_STYLE, textAlign: c.align }}>{c.label}</th>
                ))}
              </tr>
            </thead>
          </table>
        </div>
      )}

      {/* ── Scrollable body ── */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading returns…</p>
          </div>
        ) : returns.length === 0 ? (
          <Alert variant="info" className="mt-3">No returns found for the selected filters.</Alert>
        ) : (
          <table style={{ ...TABLE_STYLE, background: '#fff' }}>
            <ColGroup />
            <tbody>
              {returns.map(ret => {
                const isOpen = expandedId === ret.idReturn;
                const rowBg  = isOpen ? '#e8f0fe' : undefined;
                return (
                  <React.Fragment key={ret.idReturn}>
                    <tr
                      onClick={() => toggleExpand(ret.idReturn)}
                      style={{ cursor: 'pointer', background: rowBg }}
                      onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = '#f5f5f5'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = rowBg || ''; }}
                    >
                      <td style={{ ...TD_STYLE, textAlign: 'center', color: '#6c757d' }}>
                        <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} size="xs" />
                      </td>
                      <td style={{ ...TD_STYLE, fontWeight: 600 }}>#{ret.idReturn}</td>
                      <td style={TD_STYLE}>
                        <code className="d-block">{ret.sku || '—'}</code>
                        <span className="text-muted" style={{ fontSize: '0.78rem' }}>{ret.itemName || ''}</span>
                      </td>
                      <td style={TD_STYLE}>{ret.orderRef || '—'}</td>
                      <td style={{ ...TD_STYLE, textAlign: 'right' }}>{fmtQty(ret.authorizedQty)}</td>
                      <td style={{ ...TD_STYLE, textAlign: 'right' }}>{fmtQty(ret.returnedQty)}</td>
                      <td style={TD_STYLE}><StatusBadge status={ret.status} /></td>
                      <td style={TD_STYLE}>{fmtDate(ret.initiatedAt)}</td>
                      <td style={TD_STYLE}>
                        {ret.lastNoteText ? (
                          <div>
                            <span className="d-block text-truncate" title={ret.lastNoteText}
                              style={{ maxWidth: '100%' }}>
                              {ret.lastNoteText}
                            </span>
                            {Number(ret.noteCount) > 0 && (
                              <small className="text-muted">
                                <FontAwesomeIcon icon={faCommentDots} className="me-1" />
                                {ret.noteCount} comment{ret.noteCount !== 1 ? 's' : ''}
                              </small>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted small">No comments</span>
                        )}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={COLS.length} style={{ padding: 0, borderBottom: '1px solid #dee2e6' }}>
                          <ReturnDetail ret={ret} notesCache={notesCache} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
        {/* Infinite scroll sentinel */}
        <div ref={sentinelRef} style={{ height: 1 }} />
        {loadingMore && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" variant="primary" />
            <span className="ms-2 text-muted small">Loading more…</span>
          </div>
        )}
      </div>
    </div>
  );
}
